/** @format */
import {
  AnalyticsService,
  CookieService,
  HttpService,
  Logger,
  WINDOW,
} from '@shared/data-access/services';
import { Chain, Environment } from '@shared/data-access/interfaces';
import { DCSG_APP_NAME, DCSG_CONSTANTS } from '@shared/data-access/constants';
import { ErrorHandler, Injectable, PLATFORM_ID, inject } from '@angular/core';

import { HttpErrorResponse } from '@angular/common/http';
import { TracerService } from '@shared/ssr-core/data-access';
import { injectServerRequest } from '@shared/data-access/config';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
  public readonly ANALYTICS_ERROR_MESSAGE_PREFIX = 'DCSG_NGX_GLOBAL_ERROR:';
  private readonly request = injectServerRequest();
  private readonly analyticsService = inject(AnalyticsService);
  protected dcsgAppName = inject<string>(DCSG_APP_NAME);
  private readonly cookieService = inject(CookieService);
  private readonly httpService = inject(HttpService);
  private readonly log = inject(Logger);
  protected platformId = inject(PLATFORM_ID);
  private readonly window = inject(WINDOW);
  private readonly environment = inject(Environment);
  private readonly chain = inject(Chain);
  private readonly tracerService = inject(TracerService);

  handleError(error: Error | HttpErrorResponse) {
    // Error message
    let errorMessage: string;

    // Error details
    let errorDetails: Record<string, string> = {};

    const isBrowser = isPlatformBrowser(this.platformId);

    if (error instanceof HttpErrorResponse) {
      const { errorMessage: msg, errorDetails: details } = this.buildHttpErrorInfo(
        error,
        isBrowser,
      );
      errorMessage = msg;
      errorDetails = details;
    } else {
      errorMessage = `${DCSG_CONSTANTS.errorHandling.messagePrefixes.global.client}${error.message}`;
      errorDetails = {
        'X-ERROR-MESSAGE': `${error.message}`,
        'X-ERROR-NAME': `${error.name}`,
        'X-ERROR-STACK': `${error.stack}`,
      };
    }

    // Set the DSG_UID
    errorDetails['DSG_UID'] = this.cookieService.get('DSG_UID');

    // Set the URL
    errorDetails['X-HREF'] =
      `${this.getUrlProtocol()}://${this.httpService.getHostName()}${this.httpService.getUrlPath()}${this.getUrlQueryString()}`;

    const useWarning = this.shouldUseWarning(error);
    if (useWarning) {
      this.log.warn(`${errorMessage}`, errorDetails);
    } else {
      this.log.error(`${errorMessage}`, errorDetails);
    }

    // Check to see if context is browser & siteData was initalized
    if (isBrowser && this.window.siteData) {
      // Set and trigger analytics
      this.analyticsService.setErrorId(`${this.dcsgAppName}_GLOBAL_ERROR`);
      this.analyticsService.setErrorMessage(this.buildAnalyticsErrorMessage(errorDetails));
      this.analyticsService.triggerAnalyticsEvent('SiteError');
    }
    if (this.environment.killSwitch.honeycombEnabled[this.chain.chainIdentifierAbbr]) {
      this.tracerService.logError({
        message: errorMessage,
        ...errorDetails,
      });
    }
  }

  private buildHttpErrorInfo(
    error: HttpErrorResponse,
    isBrowser: boolean,
  ): { errorMessage: string; errorDetails: Record<string, string> } {
    const prefix =
      isBrowser && !this.window.navigator.onLine
        ? DCSG_CONSTANTS.errorHandling.messagePrefixes.global.offline
        : DCSG_CONSTANTS.errorHandling.messagePrefixes.global.http;

    let errorDetails: Record<string, string> = {};
    try {
      errorDetails = {
        'X-ERROR-STATUS': `${error.status}`,
        'X-ERROR-ERROR':
          typeof error.error === 'object' ? JSON.stringify(error.error) : `${error.error}`,
        'X-ERROR-HEADERS':
          typeof error.headers === 'object' ? JSON.stringify(error.headers) : `${error.headers}`,
        'X-ERROR-MESSAGE': `${error.message}`,
        'X-ERROR-TYPE': `${error.type}`,
        'X-ERROR-NAME': `${error.name}`,
      };
    } catch {
      // Swallow the JSON stringify error
    }

    return { errorMessage: `${prefix}${error.message}`, errorDetails };
  }

  private shouldUseWarning(error: Error | HttpErrorResponse) {
    return (
      isPlatformBrowser(this.platformId) &&
      typeof GeolocationPositionError !== 'undefined' &&
      error instanceof GeolocationPositionError
    );
  }

  private buildAnalyticsErrorMessage(errorDetails: object): string {
    return `${this.ANALYTICS_ERROR_MESSAGE_PREFIX}${JSON.stringify(errorDetails)}`;
  }

  /**
   * @description
   * Gets the current URL protocol
   */
  public getUrlProtocol(): string {
    // URL protocol string
    let urlProtocol = '';

    // Check to see if browser or server
    if (!isPlatformBrowser(this.platformId)) {
      // Get the URL protocol from Express
      urlProtocol = this.request?.protocol?.replace(':', '') ?? '';
    } else {
      // Get the URL path from the window
      urlProtocol = this.window.location.protocol.replace(':', '');
    }

    // Return the URL protocol
    return urlProtocol;
  }

  /**
   * @description
   * Gets the current URL query string
   */
  public getUrlQueryString(): string {
    // Check if running in the browser environment
    if (isPlatformBrowser(this.platformId)) {
      // Return the URL query string from the window
      return this.window.location.search;
    }

    // Get the URL query string from the server (Express)
    const urlArr = this.request?.originalUrl?.split('?') ?? [];
    return urlArr.length > 1 ? `?${urlArr[1]}` : '';
  }
}
